import {
  Alert,
  Field,
  Heading,
  Label,
  Paragraph,
  EXPERIMENTAL_Suggestion as Suggestion,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId } from "react";
import { type FieldError, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { VarmeSelskapDto } from "@/api/generated";
import { FormWrapper, TranslatedValidationMessage } from "@/components";
import {
  BestillingFormError,
  type BestillingPageKey,
  useBestillingContext,
  useLeverandoerData,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
  useBestillingNavigation,
} from "@/features/navigation";
import { isDirty } from "@/utils";
import {
  finnLeverandoerFormSchema,
  type finnLeverandoerFormValues,
  getDefaultLeverandoerFormValues,
  type LeverandoerSelected,
} from "./FinnLeverandoerFormSchemas";

type FinnLeverandoerFormProps = {
  leverandoerer: VarmeSelskapDto[];
};
const currentPageKey: BestillingPageKey = "/bestilling/ny/finn-leverandoer";

export function FinnLeverandoerForm({
  leverandoerer,
}: FinnLeverandoerFormProps) {
  const { t } = useTranslation();
  const { navigateNext } = useBestillingNavigation(currentPageKey);
  const { readOnly, bestilling, setBestillingData } = useBestillingContext();
  const { getLeverandoerByOrgNr } = useLeverandoerData();

  const initValgtLeverandoer = bestilling?.leverandoerOrgNr
    ? getLeverandoerByOrgNr(bestilling.leverandoerOrgNr)
    : undefined;

  const form = useForm({
    resolver: zodResolver(finnLeverandoerFormSchema),
    values: getDefaultLeverandoerFormValues(
      leverandoerer,
      initValgtLeverandoer
        ? {
            label: initValgtLeverandoer.navn,
            value: initValgtLeverandoer.organisasjonsnummer,
          }
        : undefined,
    ),
    disabled: readOnly,
    mode: "all",
  });

  const sortedLeverandoerer = leverandoerer
    .slice()
    .sort((a, b) => a.navn.localeCompare(b.navn, "no"));

  const updateBestillingQuery = (data: finnLeverandoerFormValues) => {
    return Promise.resolve(
      setBestillingData({
        ...bestilling,
        leverandoerOrgNr: data.leverandoer?.value ?? null,
      }),
    );
  };

  async function handleSubmit(data: finnLeverandoerFormValues) {
    if (isDirty(form.formState)) {
      await updateBestillingQuery(data);
    }
    await navigateNext();
  }

  const onLeverandoerSelectedChange = (item: LeverandoerSelected | null) => {
    form.clearErrors();
    form.setValue("leverandoer", item ?? undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const valgtLeverandoerer = form.watch("leverandoer");
  const valgtLeverandoer = getLeverandoerByOrgNr(
    valgtLeverandoerer?.value || null,
  );

  const valgtLeverandoerSelection: string | LeverandoerSelected =
    valgtLeverandoer
      ? ({
          label: valgtLeverandoer.navn,
          value: valgtLeverandoer.organisasjonsnummer,
        } as LeverandoerSelected)
      : "";
  const selectId = useId();
  return (
    <section>
      <FormNavigationBlocker form={form} onSubmit={updateBestillingQuery} />
      <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
        <Field>
          <Label htmlFor={selectId}>
            {t("pages.finn-leverandoer.velg-leverandoer-label")}
          </Label>
          <Suggestion
            name="leverandoer"
            selected={valgtLeverandoerSelection}
            onSelectedChange={onLeverandoerSelectedChange}
            multiple={false}
          >
            <Suggestion.Input readOnly={readOnly} id={selectId} />
            <Suggestion.Clear onClick={() => form.clearErrors()} />
            <Suggestion.List
              singular={t("pages.finn-leverandoer.singular")}
              plural={t("pages.finn-leverandoer.plural")}
            >
              {!sortedLeverandoerer && (
                <Suggestion.Empty>
                  <span>{t("pages.finn-leverandoer.ingen-treff")}</span>
                </Suggestion.Empty>
              )}
              {sortedLeverandoerer?.map((leverandoer) => (
                <Suggestion.Option
                  key={leverandoer.organisasjonsnummer}
                  value={leverandoer.organisasjonsnummer}
                  label={leverandoer.navn}
                >
                  {leverandoer.navn}
                </Suggestion.Option>
              ))}
            </Suggestion.List>
          </Suggestion>
          <TranslatedValidationMessage
            error={form.formState.errors.leverandoer}
          />
        </Field>

        <ValgtLeverandoerHarEgenLøsning valgtLeverandoer={valgtLeverandoer} />

        <BestillingFormError
          errors={
            form.formState.errors.harEgenLøsning as unknown as
              | FieldError
              | FieldError[]
              | undefined
          }
        />
        <BestillingNavigationActions
          currentPageKey={currentPageKey}
          disableNext={readOnly}
        />
      </FormWrapper>
    </section>
  );
}

const ValgtLeverandoerHarEgenLøsning = ({
  valgtLeverandoer,
}: {
  valgtLeverandoer: VarmeSelskapDto | null;
}) => {
  const { t } = useTranslation();
  if (!valgtLeverandoer?.harEgenLøsning) {
    return null;
  }
  return (
    <Alert data-color="info" className="space-y-4">
      <Heading data-size="xs">
        {t("pages.finn-leverandoer.norgespris.tittel", {
          navn: valgtLeverandoer.navn,
        })}
      </Heading>
      {valgtLeverandoer?.beskrivelse && (
        <Paragraph>{valgtLeverandoer.beskrivelse}</Paragraph>
      )}
    </Alert>
  );
};
